/**
 * Experimentation Engine UserContext.
 * Note: This context is auth-only and distinct from the atlasreact UserContext,
 * which also carries experiment wizard state.
 */
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RouteLocations } from '../routes/RouteLocations';
import { apiFetch } from '../utils/api';
import { syncSentryUser } from '../utils/sentryUser';

const SELECTED_CLIENT_STORAGE_KEY = 'selectedClientExternalUuid';

export interface ClientOption {
  externalUuid: string;
  name: string;
  isPersonal: boolean;
}

// Mirrors the backend UserProfile.AccountType enum.
export enum AccountType {
  INTERNAL = 'internal',
  EXTERNAL = 'external',
}

function toAccountType(value: string | null | undefined): AccountType | null {
  return value === AccountType.INTERNAL || value === AccountType.EXTERNAL ? value : null;
}
export interface AuthUser {
  email: string | null;
  name: string | null;
  accountType: AccountType | null;
  isInternalUser: boolean;
  isPoweruser: boolean;
  clients: ClientOption[];
  selectedClientExternalUuid: string | null;
  selectedClient: ClientOption | null;
  setSelectedClientExternalUuid: (externalUuid: string) => void;
  refreshUser: () => Promise<void>;
  loading: boolean;
  error?: string | null;
  avatarUrl: string
}

interface MeResponse {
  email?: string | null;
  name?: string | null;
  accountType?: string | null;
  isPoweruser?: boolean;
  csrfToken?: string;
  clients?: ClientOption[];
  avatarUrl?: string
}

const defaultUser: AuthUser = {
  email: null,
  name: null,
  accountType: null,
  isInternalUser: false,
  isPoweruser: false,
  clients: [],
  selectedClientExternalUuid: null,
  selectedClient: null,
  setSelectedClientExternalUuid: () => {},
  refreshUser: async () => {},
  loading: true,
  avatarUrl: "",
};

/**
 * Pick the client to select on load: the locally saved choice if it is still
 * available, else the user's own playground, else the first client.
 * Exported for unit testing.
 */
export function selectInitialClient(clients: ClientOption[]): string | null {
  const saved = localStorage.getItem(SELECTED_CLIENT_STORAGE_KEY);
  if (saved && clients.some((client) => client.externalUuid === saved)) return saved;
  const personal = clients.find((client) => client.isPersonal);
  if (personal) return personal.externalUuid;
  return clients[0]?.externalUuid ?? null;
}

const UserContext = createContext<AuthUser>(defaultUser);

export function useUser(): AuthUser {
  return useContext(UserContext);
}

export function UserProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [user, setUser] = useState<AuthUser>(defaultUser);
  const navigate = useNavigate();

  const refreshUser = useCallback(async (): Promise<void> => {
    try {
      const res = await apiFetch('/api/me');
      if (res.status === 401 || res.status === 403) {
        syncSentryUser(null);
        navigate('/login', { replace: true });
        return;
      }
      if (!res.ok) throw new Error(`Failed to load user data: ${res.status}`);
      const data: MeResponse = await res.json();
      syncSentryUser(data);
      const clients = data.clients ?? [];
      const selectedClientExternalUuid = selectInitialClient(clients);
      if (selectedClientExternalUuid) {
        localStorage.setItem(SELECTED_CLIENT_STORAGE_KEY, selectedClientExternalUuid);
      } else {
        localStorage.removeItem(SELECTED_CLIENT_STORAGE_KEY);
      }
      if (data.csrfToken) sessionStorage.setItem('csrfToken', data.csrfToken);
      setUser((prev) => ({
        ...prev,
        email: data.email ?? null,
        name: data.name || null,
        accountType: toAccountType(data.accountType),
        isPoweruser: data.isPoweruser ?? false,
        clients,
        selectedClientExternalUuid,
        selectedClient: clients.find((client) => client.externalUuid === selectedClientExternalUuid) ?? null,
        loading: false,
        error: null,
        avatarUrl: data?.avatarUrl ?? "",
      }));
    } catch {
      // Keep the last good state on refresh failures; the fatal error screen
      // is only for sessions that never loaded user data.
      setUser((prev) =>
        prev.email !== null
          ? prev
          : { ...prev, loading: false, error: 'Network error or failed to load user data.' },
      );
    }
  }, [navigate]);

  // Fetch once on mount. refreshUser is excluded from deps because useNavigate
  // returns a new identity on every route change, which would refetch /api/me
  // on every navigation.
  useEffect(() => {
    void refreshUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setSelectedClientExternalUuid = (externalUuid: string): void => {
    const selectedClient = user.clients.find((client) => client.externalUuid === externalUuid) ?? null;
    if (!selectedClient || externalUuid === user.selectedClientExternalUuid) return;

    localStorage.setItem(SELECTED_CLIENT_STORAGE_KEY, externalUuid);
    setUser((prev) => ({
      ...prev,
      selectedClientExternalUuid: externalUuid,
      selectedClient,
    }));
    navigate(RouteLocations.HOME);
  };

  if (user.loading) {
    return <></>;
  }

  const contextValue: AuthUser = {
    ...user,
    isInternalUser: user.accountType === AccountType.INTERNAL,
    setSelectedClientExternalUuid,
    refreshUser,
  };

  return (
    <UserContext.Provider value={contextValue}>
      {user.error ? (
        <div style={{ padding: '20px', color: '#b84e3a', backgroundColor: '#fdf0ee', textAlign: 'center' }}>
          {user.error}
        </div>
      ) : (
        children
      )}
    </UserContext.Provider>
  );
}
